// API helpers para el sistema de modelos guardados de Interfaz V2.
//
// Tabla `modelos`:
//   id (uuid)  ·  created_at (timestamptz — orden de creación)
//   id_company (uuid, nullable — NULL = modelo global de Atlas)
//   nombre (text — display name)
//   foto_file_name (text — filename del thumbnail / presentación)
//   fotos_referencia (text[] — filenames de fotos internas multi-ángulo)
//
// Storage bucket `modelos`:
//   • Modelos privados: `<idCompany>/full/<fileName>`
//   • Modelos globales:  `global/full/<fileName>`
// Las URLs siempre se firman (TTL 1h) — el bucket NO es público.

import { supabase } from '../../lib/supabase';
import { GENERATE_AUTH_TOKEN } from '../../config';

const BUCKET = 'modelos';
const SIGNED_TTL = 3600;
// Upload de modelo propio (fotos del user). El cliente guarda la imagen cruda
// + la fila, y dispara ESTE webhook fire-and-forget para que el backend lo
// procese en segundo plano (ver `uploadOwnModelDirect`).
const PROCESAR_MODELO_URL =
  'https://ai-agency-dt7d.onrender.com/webhook/procesar_modelo_propio';
// Creación de modelo desde características (modo 'crear'). Endpoint propio.
const PROCESAR_MODELO_CREADO_URL =
  'https://ai-agency-dt7d.onrender.com/webhook/procesar_modelo_creado';

export interface ModelV2 {
  id: number;
  nombre: string;
  /** null = global de Atlas, uuid = privado de esa empresa. */
  idCompany: string | null;
  /** Filename del thumbnail/presentación (la que se ve en el picker). */
  fotoFileName: string | null;
  /** Filenames de las fotos de referencia (multi-ángulo, uso interno). */
  fotosReferencia: string[];
  /** Signed URL del thumbnail — ya resuelto. */
  thumbnailUrl: string;
  /** Signed URLs de fotos_referencia — ya resueltas, en mismo orden. */
  referenceUrls: string[];
}

/**
 * Devuelve el path en el bucket `modelos` para un filename de ese modelo
 * según si es global (vive en `global/full/`) o privado (`<company>/full/`).
 */
export function getModelStoragePath(
  model: Pick<ModelV2, 'idCompany'>,
  fileName: string,
): string {
  if (model.idCompany === null) {
    return `global/full/${fileName}`;
  }
  return `${model.idCompany}/full/${fileName}`;
}

async function signFile(path: string): Promise<string> {
  const { data } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(path, SIGNED_TTL);
  return data?.signedUrl || '';
}

/**
 * Fetcha todos los modelos visibles para el user: globales + los privados
 * de su empresa. Firma todas las URLs (thumbnail + referencias) y las
 * devuelve listas para usar. Globales primero (NULLS FIRST), luego por nombre.
 */
export async function fetchUserModels(
  companyId: string,
): Promise<ModelV2[]> {
  const { data, error } = await supabase
    .from('modelos')
    .select('id, nombre, id_company, foto_file_name, fotos_referencia')
    .or(`id_company.is.null,id_company.eq.${companyId}`)
    // Más recientes primero por fecha de creación. OJO: `id` es UUID (no
    // autoincrement), así que NO sirve para ordenar — hay que usar created_at.
    // Este orden se propaga a todos los menús (sort estable downstream).
    .order('created_at', { ascending: false });
  if (error || !data) {
    console.error('[modelsApi] fetchUserModels error', error);
    return [];
  }

  // Firmar URLs en paralelo
  const models: ModelV2[] = await Promise.all(
    (data as any[]).map(async (m): Promise<ModelV2> => {
      const partial: Pick<ModelV2, 'idCompany'> = {
        idCompany: m.id_company || null,
      };
      const fotoFileName: string | null = m.foto_file_name || null;
      const fotosReferencia: string[] = Array.isArray(m.fotos_referencia)
        ? m.fotos_referencia
        : [];
      const thumbnailUrl = fotoFileName
        ? await signFile(getModelStoragePath(partial, fotoFileName))
        : '';
      const referenceUrls = await Promise.all(
        fotosReferencia.map((fn) =>
          signFile(getModelStoragePath(partial, fn)),
        ),
      );
      return {
        id: m.id as number,
        nombre: (m.nombre as string) || 'Modelo',
        idCompany: m.id_company || null,
        fotoFileName,
        fotosReferencia,
        thumbnailUrl,
        referenceUrls,
      };
    }),
  );
  return models;
}

/** Fetcha un modelo puntual por id (para el flow post-procesamiento). */
export async function fetchModelById(
  modelId: number,
): Promise<ModelV2 | null> {
  const { data, error } = await supabase
    .from('modelos')
    .select('id, nombre, id_company, foto_file_name, fotos_referencia')
    .eq('id', modelId)
    .single();
  if (error || !data) {
    console.error('[modelsApi] fetchModelById error', error);
    return null;
  }
  const m = data as any;
  const partial: Pick<ModelV2, 'idCompany'> = {
    idCompany: m.id_company || null,
  };
  const fotoFileName: string | null = m.foto_file_name || null;
  const fotosReferencia: string[] = Array.isArray(m.fotos_referencia)
    ? m.fotos_referencia
    : [];
  const thumbnailUrl = fotoFileName
    ? await signFile(getModelStoragePath(partial, fotoFileName))
    : '';
  const referenceUrls = await Promise.all(
    fotosReferencia.map((fn) => signFile(getModelStoragePath(partial, fn))),
  );
  return {
    id: m.id as number,
    nombre: (m.nombre as string) || 'Modelo',
    idCompany: m.id_company || null,
    fotoFileName,
    fotosReferencia,
    thumbnailUrl,
    referenceUrls,
  };
}

/** Actualiza solo el nombre de un modelo. */
export async function updateModelName(
  modelId: number,
  nombre: string,
): Promise<void> {
  const { error } = await supabase
    .from('modelos')
    .update({ nombre })
    .eq('id', modelId);
  if (error) throw error;
}

/**
 * Elimina un modelo privado: borra sus archivos del storage (thumbnail + fotos
 * de referencia) y la fila en `modelos`. Los modelos globales de Atlas
 * (idCompany === null) NO se pueden eliminar.
 */
export async function deleteModel(
  model: Pick<ModelV2, 'id' | 'idCompany' | 'fotoFileName' | 'fotosReferencia'>,
): Promise<void> {
  if (model.idCompany === null) {
    throw new Error('No se pueden eliminar los modelos globales de Atlas.');
  }
  const paths: string[] = [];
  if (model.fotoFileName) {
    paths.push(getModelStoragePath(model, model.fotoFileName));
  }
  for (const fn of model.fotosReferencia) {
    paths.push(getModelStoragePath(model, fn));
  }
  // Storage best-effort: aunque falle (o no exista), igual borramos la fila.
  if (paths.length > 0) {
    await supabase.storage.from(BUCKET).remove(paths);
  }
  const { error } = await supabase.from('modelos').delete().eq('id', model.id);
  if (error) throw error;
}

// ─── File → base64 (sin el prefix `data:...,`) ────────────────────────
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Strip "data:image/jpeg;base64," etc — el backend espera solo base64.
      const idx = result.indexOf(',');
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = () => reject(new Error('No se pudo leer el archivo'));
    reader.readAsDataURL(file);
  });
}

// El backend puede devolver el b64 con o sin el prefix `data:image/...;base64,`.
// Lo normalizamos a sin prefix para subirlo al storage como blob.
function stripB64Prefix(raw: string): string {
  const idx = raw.indexOf(',');
  return idx >= 0 ? raw.slice(idx + 1) : raw;
}

/**
 * Sube las imágenes del user al webhook `procesar_modelo_propio`. El
 * backend procesa las fotos y devuelve dos imágenes en base64:
 *   • `image_ref`              → imagen de presentación (la que se ve
 *                                 como thumbnail en el picker).
 *   • `image_angles_character` → imagen con la persona desde varios
 *                                 ángulos (uso interno como referencia).
 * El cliente NO guarda nada todavía — solo muestra los b64 en preview
 * hasta que el user confirma con "Utilizar" (ver `saveProcessedModel`).
 */
export async function processOwnModel({
  userId,
  files,
}: {
  userId: string;
  files: File[];
}): Promise<{ imageRefB64: string; imageAnglesB64: string }> {
  if (files.length === 0) throw new Error('Subí al menos una imagen');
  const imagenes = await Promise.all(files.map(fileToBase64));
  const response = await fetch(PROCESAR_MODELO_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: GENERATE_AUTH_TOKEN,
    },
    // `modo: 'procesar'` → el backend procesa las fotos subidas por el user.
    body: JSON.stringify({ user_id: userId, modo: 'procesar', imagenes }),
  });
  if (!response.ok) {
    const txt = await response.text().catch(() => '');
    throw new Error(txt || `Error ${response.status} procesando modelo`);
  }
  const data: any = await response.json();
  if (
    data?.response !== 'success' ||
    typeof data?.image_ref !== 'string' ||
    typeof data?.image_angles_character !== 'string'
  ) {
    throw new Error('Respuesta inesperada del servidor');
  }
  return {
    imageRefB64: stripB64Prefix(data.image_ref as string),
    imageAnglesB64: stripB64Prefix(data.image_angles_character as string),
  };
}

/**
 * Valor del tipo de cuerpo "extra grande". Se comparte con `ModelPicker` para
 * que la opción del menú y el refuerzo de prompt de abajo no se desincronicen.
 */
export const TIPO_CUERPO_EXTRA_GRANDE = 'Talle Extra Grande';

// Aclaración (NO visible para el user) que se manda en lugar del simple
// "Talle Extra Grande" cuando se elige esa opción. El backend tendía a generar
// cuerpos apenas rellenos, así que reforzamos que debe ser realmente de talle
// extra grande / con sobrepeso evidente.
const TIPO_CUERPO_EXTRA_GRANDE_PROMPT =
  'Talle Extra Grande. IMPORTANTE: la persona debe ser realmente de talle ' +
  'extra grande, con sobrepeso evidente / obesidad — NO apenas un poco ' +
  'rellena. Cuerpo notoriamente corpulento y voluminoso, como alguien con ' +
  'sobrepeso real.';

/** Características del modelo personalizado (espejo de `CustomModelFields`). */
export interface CreateModelFields {
  genero?: string;
  edad?: string;
  tipoCuerpo?: string;
  tonoPiel?: string;
  colorPelo?: string;
  largoPelo?: string;
  descripcion?: string;
}

/**
 * Crea un modelo desde cero a partir de las características elegidas por el
 * user (sin fotos). Usa el MISMO webhook que `processOwnModel` pero con
 * `modo: 'crear'` y un objeto `caracteristicas` en vez de `imagenes`. El
 * backend genera y devuelve los mismos dos b64 (`image_ref` +
 * `image_angles_character`), por lo que el guardado (`saveProcessedModel`)
 * se reutiliza tal cual.
 */
export async function createModelFromFields({
  userId,
  fields,
}: {
  userId: string;
  fields: CreateModelFields;
}): Promise<{ imageRefB64: string; imageAnglesB64: string }> {
  const caracteristicas: Record<string, string> = {};
  if (fields.genero) caracteristicas.genero = fields.genero;
  if (fields.edad) caracteristicas.edad = fields.edad;
  if (fields.tipoCuerpo)
    caracteristicas.tipo_cuerpo =
      fields.tipoCuerpo === TIPO_CUERPO_EXTRA_GRANDE
        ? TIPO_CUERPO_EXTRA_GRANDE_PROMPT
        : fields.tipoCuerpo;
  if (fields.tonoPiel) caracteristicas.tono_piel = fields.tonoPiel;
  if (fields.colorPelo) caracteristicas.color_pelo = fields.colorPelo;
  if (fields.largoPelo) caracteristicas.largo_pelo = fields.largoPelo;
  if (fields.descripcion) caracteristicas.descripcion = fields.descripcion;

  const response = await fetch(PROCESAR_MODELO_CREADO_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: GENERATE_AUTH_TOKEN,
    },
    body: JSON.stringify({ user_id: userId, modo: 'crear', caracteristicas }),
  });
  if (!response.ok) {
    const txt = await response.text().catch(() => '');
    throw new Error(txt || `Error ${response.status} creando modelo`);
  }
  const data: any = await response.json();
  if (
    data?.response !== 'success' ||
    typeof data?.image_ref !== 'string' ||
    typeof data?.image_angles_character !== 'string'
  ) {
    throw new Error('Respuesta inesperada del servidor');
  }
  return {
    imageRefB64: stripB64Prefix(data.image_ref as string),
    imageAnglesB64: stripB64Prefix(data.image_angles_character as string),
  };
}

// ─── Save: cliente guarda en storage + DB ──────────────────────────────
// b64 → Blob para subir al bucket.
function b64ToBlob(b64: string, type = 'image/png'): Blob {
  const bytes = atob(b64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new Blob([arr], { type });
}

// Slug + timestamp para filenames únicos.
function buildModelFileName(seed: string, ext: string): string {
  const slug = seed
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 40);
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${
    slug || 'modelo'
  }.${ext}`;
}

/**
 * Persiste el modelo procesado. Los dos b64 vienen del webhook (ver
 * `processOwnModel`):
 *   1. Sube `presentationB64` (image_ref) → `modelos/<company>/full/<fileName>.png`
 *      → queda como `foto_file_name` (thumbnail visible en el picker).
 *   2. Sube `anglesB64` (image_angles_character) al mismo path → entrada
 *      única en `fotos_referencia[]` (uso interno multi-ángulo).
 *   3. INSERT en `modelos`.
 *   4. Devuelve el `ModelV2` recién creado con signed URLs resueltas.
 *
 * Se llama cuando el user toca "Utilizar" en la UI.
 */
export async function saveProcessedModel({
  companyId,
  name,
  presentationB64,
  anglesB64,
}: {
  companyId: string;
  name: string;
  presentationB64: string;
  anglesB64: string;
}): Promise<ModelV2> {
  // Nombre placeholder si el user no escribió ninguno — "Modelo N+1".
  let effectiveName = name.trim();
  if (!effectiveName) {
    const { count } = await supabase
      .from('modelos')
      .select('id', { count: 'exact', head: true })
      .eq('id_company', companyId);
    effectiveName = `Modelo ${(count ?? 0) + 1}`;
  }

  // 1. Subir presentación (image_ref → PNG blob)
  const presentationBlob = b64ToBlob(presentationB64, 'image/png');
  const presentationName = buildModelFileName(effectiveName, 'png');
  const presentationPath = `${companyId}/full/${presentationName}`;
  const { error: pErr } = await supabase.storage
    .from(BUCKET)
    .upload(presentationPath, presentationBlob, {
      cacheControl: '3600',
      upsert: false,
      contentType: 'image/png',
    });
  if (pErr) throw pErr;

  // 2. Subir image_angles_character (PNG blob) — una sola entrada en
  //    fotos_referencia[], aunque la imagen contenga múltiples ángulos.
  const anglesBlob = b64ToBlob(anglesB64, 'image/png');
  const anglesName = buildModelFileName(`${effectiveName}-angles`, 'png');
  const anglesPath = `${companyId}/full/${anglesName}`;
  const { error: aErr } = await supabase.storage
    .from(BUCKET)
    .upload(anglesPath, anglesBlob, {
      cacheControl: '3600',
      upsert: false,
      contentType: 'image/png',
    });
  if (aErr) throw aErr;

  // 3. INSERT en modelos
  const { data: inserted, error: insErr } = await supabase
    .from('modelos')
    .insert({
      id_company: companyId,
      nombre: effectiveName,
      foto_file_name: presentationName,
      fotos_referencia: [anglesName],
    })
    .select('id')
    .single();
  if (insErr) throw insErr;

  // 4. Fetch + return el modelo completo (signed URLs incluidas)
  const created = await fetchModelById((inserted as any).id as number);
  if (!created) {
    throw new Error('Modelo guardado pero no se pudo cargar de vuelta');
  }
  return created;
}

/**
 * Dispara `procesar_modelo_propio` SIN esperar la respuesta (fire-and-forget).
 * Manda el mismo payload de antes (`user_id`, `modo: 'procesar'`, `imagenes`)
 * + el `modelo_id` de la fila recién creada, para que el backend procese la
 * imagen en segundo plano y actualice ESE modelo. Cualquier error se loguea y
 * se ignora (no bloquea el guardado, que ya ocurrió).
 */
function fireProcesarModeloPropio(args: {
  userId: string;
  modeloId: number | string;
  file: File;
}): void {
  void (async () => {
    try {
      const imagenes = [await fileToBase64(args.file)];
      await fetch(PROCESAR_MODELO_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: GENERATE_AUTH_TOKEN,
        },
        body: JSON.stringify({
          user_id: args.userId,
          modo: 'procesar',
          imagenes,
          modelo_id: args.modeloId,
        }),
      });
    } catch (e) {
      console.error(
        '[modelsApi] procesar_modelo_propio (fire-and-forget) error',
        e,
      );
    }
  })();
}

/**
 * Upload de modelo propio "directo" (Interfaz V2): guarda la imagen CRUDA que
 * subió el user como thumbnail + inserta la fila en `modelos`, y dispara el
 * webhook `procesar_modelo_propio` fire-and-forget (con el id del modelo) para
 * que el backend lo procese async. NO espera el procesamiento ni muestra
 * preview — devuelve el modelo recién guardado al instante.
 *
 * (El flujo viejo `processOwnModel` + `saveProcessedModel` esperaba el webhook
 * y guardaba la imagen YA procesada; este lo reemplaza en el UploadFlow.)
 */
export async function uploadOwnModelDirect({
  companyId,
  userId,
  name,
  file,
}: {
  companyId: string;
  userId: string;
  name: string;
  file: File;
}): Promise<ModelV2> {
  // Nombre placeholder si el user no escribió ninguno — "Modelo N+1".
  let effectiveName = name.trim();
  if (!effectiveName) {
    const { count } = await supabase
      .from('modelos')
      .select('id', { count: 'exact', head: true })
      .eq('id_company', companyId);
    effectiveName = `Modelo ${(count ?? 0) + 1}`;
  }

  // 1. Subir la imagen cruda → modelos/<company>/full/<fileName>
  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
  const fileName = buildModelFileName(effectiveName, ext);
  const fullPath = `${companyId}/full/${fileName}`;
  const { error: upErr } = await supabase.storage
    .from(BUCKET)
    .upload(fullPath, file, {
      cacheControl: '3600',
      upsert: false,
      contentType: file.type || 'image/jpeg',
    });
  if (upErr) throw upErr;

  // 2. INSERT en modelos (solo thumbnail; las fotos_referencia las completa
  //    el backend al procesar, si corresponde).
  const { data: inserted, error: insErr } = await supabase
    .from('modelos')
    .insert({
      id_company: companyId,
      nombre: effectiveName,
      foto_file_name: fileName,
    })
    .select('id')
    .single();
  if (insErr) throw insErr;
  const modeloId = (inserted as any).id;

  // 3. Disparar el procesamiento async (no esperamos respuesta).
  fireProcesarModeloPropio({ userId, modeloId, file });

  // 4. Fetch + return el modelo recién creado (signed URL del thumbnail).
  const created = await fetchModelById(modeloId as number);
  if (!created) {
    throw new Error('Modelo guardado pero no se pudo cargar de vuelta');
  }
  return created;
}
